import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle, XCircle, PaperPlaneTilt, MapPin, Wallet, Airplane, Car } from "@phosphor-icons/react";

const CAT_COLORS = {
  airfare: "lb-badge-info", lodging: "lb-badge-teal", meals: "lb-badge-warning",
  transport: "lb-badge-info", fuel: "lb-badge-warning", mileage: "lb-badge-teal",
  per_diem: "lb-badge", office: "lb-badge", software: "lb-badge",
};
const STATUS_COLORS = {
  draft: "", submitted: "lb-badge-warning", approved: "lb-badge-success",
  rejected: "lb-badge-danger", reimbursed: "lb-badge-info",
};

export default function TravelExpense() {
  const [tab, setTab] = useState("expenses");
  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [showExp, setShowExp] = useState(false);
  const [showTrip, setShowTrip] = useState(false);
  const [expForm, setExpForm] = useState({
    date: new Date().toISOString().slice(0,10), category: "meals", amount: 0,
    currency: "INR", merchant: "", description: "", trip_id: "",
    payment_method: "personal", mileage_km: "", receipt_base64: null,
  });
  const [tripForm, setTripForm] = useState({
    name: "", purpose: "", destination: "",
    start_date: new Date().toISOString().slice(0,10),
    end_date: "", budget: 0, currency: "INR",
  });

  const load = async () => {
    const [c, s, e, t] = await Promise.all([
      api.get("/tne/config"), api.get("/tne/summary"),
      api.get("/tne/expenses"), api.get("/tne/trips"),
    ]);
    setCfg(c.data); setSummary(s.data); setExpenses(e.data); setTrips(t.data);
  };
  useEffect(() => { load(); }, []);

  const handleReceipt = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 500000) return toast.error("Max 500KB for receipt");
    const reader = new FileReader();
    reader.onload = () => setExpForm({ ...expForm, receipt_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const submitExp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tne/expenses", { ...expForm,
        amount: Number(expForm.amount),
        mileage_km: expForm.mileage_km ? Number(expForm.mileage_km) : null,
        trip_id: expForm.trip_id || null });
      toast.success("Expense saved as draft");
      setShowExp(false);
      setExpForm({ date: new Date().toISOString().slice(0,10), category: "meals", amount: 0,
                   currency: "INR", merchant: "", description: "", trip_id: "",
                   payment_method: "personal", mileage_km: "", receipt_base64: null });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const submitTrip = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tne/trips", { ...tripForm, budget: Number(tripForm.budget) });
      toast.success("Trip created");
      setShowTrip(false);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const doAction = async (id, action, notes = "") => {
    try {
      await api.post(`/tne/expenses/${id}/${action}`, action === "submit" ? undefined : { notes });
      toast.success(action);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  if (!cfg || !summary) return <div className="text-neutral-500">Loading…</div>;

  const filtered = tab === "expenses" ? expenses :
                   tab === "approvals" ? expenses.filter((e) => e.status === "submitted") :
                   tab === "reimburse" ? expenses.filter((e) => e.status === "approved") : [];

  return (
    <div className="space-y-5 lb-fade" data-testid="tne-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="lb-section-head">Travel & Expense</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">T&E Management</h1>
          <p className="text-neutral-600 text-sm mt-1">Submit expenses · Track trips · Approve & reimburse — with receipts, mileage, and per-diem.</p>
        </div>
        <div className="flex gap-2">
          <button className="lb-btn lb-btn-ghost" onClick={() => setShowTrip((s) => !s)} data-testid="add-trip-btn">
            <MapPin size={14} /> New Trip
          </button>
          <button className="lb-btn" onClick={() => setShowExp((s) => !s)} data-testid="add-expense-btn">
            <Plus size={14} /> New Expense
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        <div className="lb-kpi lb-kpi-count"><div className="lb-kpi-label">Total Spend</div><div className="lb-kpi-value mono">{fmtINR(summary.total_spend)}</div></div>
        <div className="lb-kpi lb-kpi-teal"><div className="lb-kpi-label">Trips</div><div className="lb-kpi-value">{summary.open_trips}<span className="text-sm text-neutral-500"> / {summary.trips}</span></div></div>
        <div className="lb-kpi lb-kpi-gst"><div className="lb-kpi-label">My Drafts</div><div className="lb-kpi-value">{summary.my_pending_count}</div></div>
        <div className="lb-kpi lb-kpi-outstanding"><div className="lb-kpi-label">To Approve</div><div className="lb-kpi-value">{summary.to_approve_count}</div></div>
        <div className="lb-kpi lb-kpi-revenue"><div className="lb-kpi-label">To Reimburse</div><div className="lb-kpi-value">{summary.to_reimburse_count}</div></div>
      </div>

      {showExp && (
        <form onSubmit={submitExp} className="lb-card p-6 grid grid-cols-4 gap-4" data-testid="expense-form">
          <div>
            <label className="lb-label">Date</label>
            <input type="date" required className="lb-input" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Category</label>
            <select className="lb-select" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} data-testid="exp-category">
              {cfg.categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          {expForm.category === "mileage" ? (
            <div>
              <label className="lb-label">Kilometers</label>
              <input type="number" step="0.1" className="lb-input font-mono text-right" value={expForm.mileage_km}
                     onChange={(e) => setExpForm({ ...expForm, mileage_km: e.target.value })} data-testid="exp-mileage" />
              <div className="text-[10px] text-neutral-500 mt-1">Rate: ₹{cfg.mileage_rate}/km</div>
            </div>
          ) : (
            <div>
              <label className="lb-label">Amount ({expForm.currency})</label>
              <input type="number" step="0.01" required={expForm.category !== "mileage"} className="lb-input font-mono text-right"
                     value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="exp-amount" />
            </div>
          )}
          <div>
            <label className="lb-label">Currency</label>
            <select className="lb-select" value={expForm.currency} onChange={(e) => setExpForm({ ...expForm, currency: e.target.value })}>
              {["INR","USD","EUR","GBP","AED","SGD"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="lb-label">Merchant</label>
            <input className="lb-input" value={expForm.merchant} onChange={(e) => setExpForm({ ...expForm, merchant: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Payment Method</label>
            <select className="lb-select" value={expForm.payment_method} onChange={(e) => setExpForm({ ...expForm, payment_method: e.target.value })}>
              <option value="personal">Personal</option>
              <option value="company_card">Company Card</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="lb-label">Trip (optional)</label>
            <select className="lb-select" value={expForm.trip_id} onChange={(e) => setExpForm({ ...expForm, trip_id: e.target.value })}>
              <option value="">Not linked to a trip</option>
              {trips.filter((t) => t.status === "open").map((t) => <option key={t.id} value={t.id}>{t.name} · {t.destination}</option>)}
            </select>
          </div>
          <div className="col-span-3">
            <label className="lb-label">Description</label>
            <input className="lb-input" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Receipt (≤500 KB)</label>
            <input type="file" accept="image/*,.pdf" onChange={handleReceipt}
                   className="text-xs" data-testid="exp-receipt" />
            {expForm.receipt_base64 && <div className="text-[10px] text-emerald-600 mt-1">✓ attached</div>}
          </div>
          <div className="col-span-4 flex gap-2 pt-2 border-t border-neutral-200">
            <button className="lb-btn" data-testid="exp-submit">Save Draft</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShowExp(false)}>Cancel</button>
          </div>
        </form>
      )}

      {showTrip && (
        <form onSubmit={submitTrip} className="lb-card p-6 grid grid-cols-4 gap-4">
          <div className="col-span-2">
            <label className="lb-label">Trip Name</label>
            <input required className="lb-input" value={tripForm.name} onChange={(e) => setTripForm({ ...tripForm, name: e.target.value })} data-testid="trip-name" />
          </div>
          <div>
            <label className="lb-label">Destination</label>
            <input required className="lb-input" value={tripForm.destination} onChange={(e) => setTripForm({ ...tripForm, destination: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Budget ({tripForm.currency})</label>
            <input type="number" step="0.01" className="lb-input font-mono text-right" value={tripForm.budget} onChange={(e) => setTripForm({ ...tripForm, budget: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Start Date</label>
            <input type="date" required className="lb-input" value={tripForm.start_date} onChange={(e) => setTripForm({ ...tripForm, start_date: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">End Date</label>
            <input type="date" required className="lb-input" value={tripForm.end_date} onChange={(e) => setTripForm({ ...tripForm, end_date: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="lb-label">Purpose</label>
            <input required className="lb-input" value={tripForm.purpose} onChange={(e) => setTripForm({ ...tripForm, purpose: e.target.value })} />
          </div>
          <div className="col-span-4 flex gap-2">
            <button className="lb-btn" data-testid="trip-submit">Create Trip</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShowTrip(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Tabs */}
      <div className="lb-card">
        <div className="flex border-b border-neutral-200">
          {[
            { k: "expenses", l: `All Expenses (${expenses.length})` },
            { k: "trips", l: `Trips (${trips.length})` },
            { k: "approvals", l: `To Approve (${summary.to_approve_count})` },
            { k: "reimburse", l: `To Reimburse (${summary.to_reimburse_count})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    className={"px-4 py-3 text-sm font-semibold border-b-2 transition-colors " +
                      (tab === t.k ? "border-[#0A66C2] text-[#0A66C2]" : "border-transparent text-neutral-500 hover:text-[#0A66C2]")}
                    data-testid={`tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "trips" ? (
          <table className="lb-table">
            <thead><tr><th>Name</th><th>Destination</th><th>Dates</th><th>Traveler</th><th className="text-right">Budget</th><th className="text-right">Spent</th><th className="text-right">Approved</th><th>Status</th></tr></thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id}>
                  <td className="font-semibold">{t.name}</td>
                  <td>{t.destination}</td>
                  <td className="text-xs font-mono">{fmtDate(t.start_date)} → {fmtDate(t.end_date)}</td>
                  <td className="text-sm">{t.traveler_name}</td>
                  <td className="amt">{fmtINR(t.budget)}</td>
                  <td className="amt">{fmtINR(t.spent)}</td>
                  <td className="amt text-emerald-600">{fmtINR(t.approved_amount)}</td>
                  <td><span className={"lb-badge " + (t.status === "open" ? "lb-badge-success" : "")}>{t.status}</span></td>
                </tr>
              ))}
              {!trips.length && <tr><td colSpan={8} className="text-center py-10 text-neutral-500">No trips yet</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="lb-table">
            <thead><tr><th>Date</th><th>Employee</th><th>Category</th><th>Description</th><th>Trip</th><th className="text-right">Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`exp-row-${e.id}`}>
                  <td className="font-mono text-sm">{fmtDate(e.date)}</td>
                  <td className="text-sm">{e.employee_name}</td>
                  <td>
                    <span className={"lb-badge " + (CAT_COLORS[e.category] || "")}>
                      {cfg.categories.find((c) => c.code === e.category)?.name || e.category}
                    </span>
                  </td>
                  <td className="text-sm">
                    {e.merchant && <span className="font-semibold">{e.merchant}</span>}
                    {e.description && <div className="text-xs text-neutral-500">{e.description}</div>}
                  </td>
                  <td className="text-xs">{trips.find((t) => t.id === e.trip_id)?.name || "—"}</td>
                  <td className="amt">{e.currency} {e.amount.toFixed(2)}</td>
                  <td><span className={"lb-badge " + STATUS_COLORS[e.status]}>{e.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      {e.status === "draft" && (
                        <button onClick={() => doAction(e.id, "submit")} className="lb-btn lb-btn-sm" data-testid={`submit-${e.id}`}>
                          <PaperPlaneTilt size={11} /> Submit
                        </button>
                      )}
                      {e.status === "submitted" && (
                        <>
                          <button onClick={() => doAction(e.id, "approve")} className="lb-btn lb-btn-sm lb-btn-success" data-testid={`approve-${e.id}`}>
                            <CheckCircle size={11} /> Approve
                          </button>
                          <button onClick={() => { const n = prompt("Rejection reason?"); if (n) doAction(e.id, "reject", n); }}
                                  className="lb-btn lb-btn-sm lb-btn-danger">
                            <XCircle size={11} />
                          </button>
                        </>
                      )}
                      {e.status === "approved" && (
                        <button onClick={() => doAction(e.id, "reimburse")} className="lb-btn lb-btn-sm lb-btn-accent" data-testid={`reimburse-${e.id}`}>
                          <Wallet size={11} /> Reimburse
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={8} className="text-center py-10 text-neutral-500">No expenses in this view</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
